/**
 * Site source card - lossless Figma/HTML switching, mirrors Figmafolio.
 *
 * Extracted verbatim from SiteSettingsTab so the card has exactly one
 * implementation with one home per site type: the Prototype tab renders it for
 * figma sites, the Settings tab renders it for html sites. The two source
 * datasets are disjoint and coexist on one doc, so switching never deletes the
 * other source. When the target source already has stored data the switch is a
 * pure type flip that re-activates it (no re-entry, no overwrite) exactly like
 * FolioForm.handleConfirmSwitch. Only when the target has no stored data yet do
 * we collect it: the inline dropzone for files, the embed modal for Figma.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import firebase from '../../firebase';
import { errorLine } from '../../components/Aero/AeroStatePanel';
import { getFilesFromEvent } from '../../utils/htmlDeploy/gatherFiles';
import { isValidReadableSlug, generateReadableSlug } from './sitesUtils';
import FigmaLinkModal from './FigmaLinkModal';

function SiteSourceCard({ user, ownerUid, site, fetchSite, fetchVersions, deploying, onDrop }) {
  const db = firebase.firestore();
  // Shared sites write to the OWNER's path, not the logged-in admin's.
  const siteRef = db
    .collection('user').doc(ownerUid || user.uid)
    .collection('url').doc(site.id);

  const isFigmaSource = site.type === 'figma';
  const hasFigmaContent =
    (Array.isArray(site.viewports) && site.viewports.some((v) => v.figmaUrl)) ||
    !!(site.urls && (site.urls.figmaDesktopUrl || site.urls.figmaMobileUrl));
  const hasHtmlContent = !!site.activeVersion;

  const [showFigmaModal, setShowFigmaModal] = useState(false);
  const [showSourceDropzone, setShowSourceDropzone] = useState(false);
  // The source a lossless flip is being confirmed for ('figma' | 'html'), or null.
  const [switchTarget, setSwitchTarget] = useState(null);
  const [switching, setSwitching] = useState(false);

  const sourceDropzone = useDropzone({
    onDrop,
    getFilesFromEvent,
    disabled: deploying,
    multiple: true,
  });

  const refreshAfterSourceChange = async () => {
    await fetchSite();
    if (fetchVersions) await fetchVersions();
    setShowSourceDropzone(false);
  };

  // Flip ONLY the type field (and, figma->html, regenerate the slug when the
  // current one is not a valid readable slug, the one extra write Figmafolio
  // makes). viewports/urls/activeVersion/versions are never touched, so the
  // other source's data is preserved and re-activates on the way back.
  const flipType = async (target) => {
    if (switching) return;
    setSwitching(true);
    try {
      const updates = { type: target, updatedAt: new Date() };
      if (target === 'html' && !isValidReadableSlug(site.generatedUrl)) {
        updates.generatedUrl = await generateReadableSlug(db);
      }
      await siteRef.set(updates, { merge: true });
      setSwitchTarget(null);
      await refreshAfterSourceChange();
      toast.success(
        target === 'figma'
          ? 'Switched to your Figma prototype.'
          : 'Switched to your uploaded files.'
      );
    } catch (err) {
      console.error('Error switching source:', err);
      toast.error('Could not switch source. ' + errorLine(err));
    } finally {
      setSwitching(false);
    }
  };

  // Lossless flip when the target already has stored data; otherwise fall back
  // to collecting it (upload a folder / embed a prototype).
  const handleSwitchToFiles = () => {
    if (hasHtmlContent) setSwitchTarget('html');
    else setShowSourceDropzone((v) => !v);
  };
  const handleSwitchToFigma = () => {
    if (hasFigmaContent) setSwitchTarget('figma');
    else setShowFigmaModal(true);
  };

  return (
    <>
      {/* Site source - compact: current source + one switch action. */}
      <div className="aero-card aero-card-pad">
        <div className="aero-section-label">Site source</div>
        <div className="aero-setting-row">
          <div>
            <h4>{isFigmaSource ? 'Figma prototype (embedded)' : 'Uploaded files'}</h4>
            <p>
              {isFigmaSource
                ? hasHtmlContent
                  ? 'Serving your embedded Figma prototype. Your uploaded files are kept, switch back anytime.'
                  : 'Serving your embedded Figma prototype. You can switch to uploaded files instead.'
                : hasFigmaContent
                  ? 'Serving your uploaded files. Your Figma prototype is kept, switch back anytime.'
                  : 'Serving your uploaded files. You can embed a Figma prototype instead.'}
            </p>
          </div>
          {isFigmaSource ? (
            <button
              type="button"
              className="aero-btn aero-btn-secondary aero-btn-sm"
              onClick={handleSwitchToFiles}
              disabled={switching}
            >
              Switch to uploaded files
            </button>
          ) : (
            <button
              type="button"
              className="aero-btn aero-btn-secondary aero-btn-sm"
              onClick={handleSwitchToFigma}
              disabled={switching}
            >
              Switch to a Figma prototype
            </button>
          )}
        </div>
        {/* Collect path (no stored files yet): inline upload dropzone. */}
        {isFigmaSource && showSourceDropzone && !hasHtmlContent && (
          <div
            {...sourceDropzone.getRootProps({
              className: `aero-sites-dropzone${sourceDropzone.isDragActive ? ' active' : ''}${
                deploying ? ' disabled' : ''
              }`,
            })}
          >
            <input
              {...sourceDropzone.getInputProps({ webkitdirectory: 'true', directory: 'true' })}
            />
            {deploying ? (
              <p>Publishing…</p>
            ) : sourceDropzone.isDragActive ? (
              <p>Drop the folder to publish</p>
            ) : (
              <>
                <p>Drag your site folder here, or click to choose one</p>
                <p className="aero-sites-dropzone-hint">
                  Publishing files makes this an uploaded-files site. Your Figma
                  prototype is kept, not deleted.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {showFigmaModal && (
        <FigmaLinkModal
          user={user}
          ownerUid={ownerUid}
          site={site}
          onClose={() => setShowFigmaModal(false)}
          onEmbedded={refreshAfterSourceChange}
        />
      )}

      {/* Lossless-switch confirm modal (mirrors Figmafolio's switchModal: the
          other source's data is kept, not deleted, and the switch is
          reversible). */}
      {switchTarget &&
        ReactDOM.createPortal(
          <div
            className="ut-modal-root aero-modal-backdrop ut-theme"
            onClick={() => !switching && setSwitchTarget(null)}
          >
            <div className="aero-modal" onClick={(e) => e.stopPropagation()}>
              <div className="aero-modal-head">
                <h2>
                  {switchTarget === 'html'
                    ? 'Switch to your uploaded files?'
                    : 'Switch to your Figma prototype?'}
                </h2>
                <button
                  type="button"
                  className="aero-btn aero-btn-ghost aero-btn-icon"
                  onClick={() => setSwitchTarget(null)}
                  disabled={switching}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="aero-modal-body">
                <p style={{ margin: 0 }}>
                  {switchTarget === 'html' ? (
                    <>
                      This site will serve your uploaded files at{' '}
                      <strong>{site.generatedUrl}.aeroapp.ai</strong>. Your Figma
                      prototype is kept, not deleted, so you can switch back anytime.
                    </>
                  ) : (
                    <>
                      This site will embed your Figma prototype at{' '}
                      <strong>aeroapp.ai/{site.generatedUrl}</strong>. Your uploaded
                      files are kept, not deleted, so you can switch back anytime.
                    </>
                  )}
                </p>
              </div>
              <div className="aero-modal-foot">
                <button
                  type="button"
                  className="aero-btn aero-btn-secondary"
                  onClick={() => setSwitchTarget(null)}
                  disabled={switching}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="aero-btn aero-btn-primary"
                  onClick={() => flipType(switchTarget)}
                  disabled={switching}
                >
                  {switching
                    ? 'Switching…'
                    : switchTarget === 'html'
                      ? 'Switch to files'
                      : 'Switch to Figma'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default SiteSourceCard;
